import { ApiError } from '@/lib/api';
import { npcRefusalOf } from '@/lib/npc-labels';
import { sessionTime } from '@/lib/session-labels';
import type { NpcWatchHeader } from '@matter_extractor/shared';

/**
 * What the bar of « Regarder » says (npc.md §11, M5, issue #210), PURE: the state of the replay, its
 * turn, and the sentence of the cases where there is nothing to watch. Nothing of the machine is in
 * the bar: no step, no objective, no cost.
 */

/** Where the bar's state pill stands: a replay under way, one that was just over, or the recording. */
export type WatchMode = 'live' | 'over' | 'playback';

/** « en direct », « en différé », « fini à 15:31 » (or « fini » when the end has no instant). */
export function watchStateLabel(mode: WatchMode, endedAt: string | null): string {
  if (mode === 'live') return 'en direct';
  if (mode === 'playback') return 'en différé';
  return endedAt === null ? 'fini' : `fini à ${sessionTime(endedAt)}`;
}

/** « tour 7 », or nothing before the first turn. */
export function watchTurnLabel(turn: number): string | null {
  return turn > 0 ? `tour ${turn}` : null;
}

/** « Le Train v24 », or just the name when the version is not known. */
export function watchExperienceLabel(
  header: Pick<NpcWatchHeader, 'experienceName' | 'experienceVersionNumber'>,
): string {
  return header.experienceVersionNumber === null
    ? header.experienceName
    : `${header.experienceName} v${header.experienceVersionNumber}`;
}

/** « page 4 / 8 · tour 7 / 22 » under the line of progress of the recording. */
export function playbackPositionLabel(frame: {
  page: number;
  pages: number;
  turn: number;
  turns: number;
}): string {
  const page = `page ${frame.page} / ${frame.pages}`;
  return frame.turns === 0 ? page : `${page} · tour ${frame.turn} / ${frame.turns}`;
}

/**
 * What a failed read says (§18): a replay that was purged has no session left; a replay that still
 * runs has no recording yet; anything else is a read that did not go through.
 */
export function watchErrorSentence(error: unknown): string {
  const code = npcRefusalOf(error)?.code;
  if (
    (error instanceof ApiError && error.status === 404) ||
    code === 'replay-unknown' ||
    code === 'not-a-replay' ||
    code === 'session-in-trash'
  ) {
    return 'Ce rejeu n’existe plus : sa session a été supprimée.';
  }
  if (code === 'replay-running') {
    return 'Ce rejeu tourne encore : il n’y a pas encore de suite enregistrée à revoir.';
  }
  return 'Le rejeu n’a pas pu être lu. Réessayez dans un instant.';
}
